import { useState } from 'react';
import { TrendingDown, Zap, FileText, ArrowUp, ArrowRight, Sparkles } from 'lucide-react';
import type { DeepSignalData } from '@/api/deep-signal-api';
import type { AgentTile } from '../types';

export function SignalAnalystInput({ onSubmit }: { onSubmit: (q: string) => void }) {
  const [value, setValue] = useState('');

  const handleSubmit = () => {
    const q = value.trim();
    if (!q) return;
    onSubmit(q);
    setValue('');
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '13px',
      background: 'var(--card)',
      border: '1px solid var(--border)',
      borderRadius: '14px',
      padding: '10px 12px',
      boxShadow: '0 1px 2px rgba(13,13,15,.04), 0 12px 32px -18px rgba(126,95,224,.4)',
      marginBottom: '14px',
    }}>
      {/* Orb — shared AI identity, matches the Ask Upmode bar */}
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: 10,
        background: 'linear-gradient(135deg, var(--spectral-violet) 0%, var(--spectral-violet) 62%, var(--signal-blue) 155%)',
        boxShadow: 'var(--shadow-spectral-violet-glow)',
        flexShrink: 0,
      }}>
        <Sparkles size={15} color="#fff" />
      </span>
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
        placeholder="Ask anything about this run..."
        style={{
          flex: 1,
          border: 'none',
          outline: 'none',
          fontSize: 'var(--text-sm)',
          backgroundColor: 'transparent',
          color: 'var(--foreground)',
          minWidth: 0,
        }}
      />
      <button
        onClick={handleSubmit}
        style={{
          width: '34px',
          height: '34px',
          borderRadius: '50%',
          backgroundColor: value.trim() ? '#7e5fe0' : 'var(--border)',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: value.trim() ? 'pointer' : 'default',
          flexShrink: 0,
          transition: 'background-color 0.15s',
        }}
      >
        <ArrowUp size={15} color={value.trim() ? '#fff' : 'var(--muted-foreground)'} />
      </button>
    </div>
  );
}

export function SignalAnalystTiles({ apiData, onTileClick }: { apiData: DeepSignalData; onTileClick: (q: string) => void }) {
  const [hoveredTile, setHoveredTile] = useState<string | null>(null);

  const brand = apiData.customer.brand_name;

  const allTiles: AgentTile[] = [
    {
      id: 'where-are-we-losing',
      type: 'action',
      icon: <TrendingDown size={16} />,
      title: 'Where are we losing to competitors?',
      description: 'Find the gaps where competing brands are pulling ahead in AI recommendations',
      question: `Looking at the competitive data for ${brand}, where are competing brands outperforming us in AI recommendations and mentions? Which specific areas show the largest gaps?`,
    },
    {
      id: 'move-to-recommendation',
      type: 'action',
      icon: <ArrowUp size={16} />,
      title: 'What would move us to a top recommendation?',
      description: 'Identify the content and positioning changes that close the mention-to-recommendation gap',
      question: `${brand} is being mentioned by AI models but not consistently reaching top recommendation status. What specific content, positioning, or messaging changes would most improve our recommendation rate across models?`,
    },
    {
      id: 'prioritize-variables',
      type: 'action',
      icon: <Zap size={16} />,
      title: 'Which variables should we fix first?',
      description: 'Rank the encoding gaps by impact and effort to focus our next content push',
      question: `Based on the variable intelligence data for ${brand}, which brand attributes have the weakest AI encoding and would deliver the most impact if improved? Give me a prioritized list with reasoning.`,
    },
    {
      id: 'exec-summary',
      type: 'action',
      icon: <FileText size={16} />,
      title: 'Summarize this for my leadership team',
      description: 'Plain-language briefing with the 3 things that matter most from this run',
      question: `Write a concise executive summary of the ${brand} AI visibility baseline findings. Focus on the 3 most important takeaways, what they mean for the business, and what we should do next. Use plain language suitable for a leadership team.`,
    },
  ];

  return (
    <div>
      <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--muted-foreground)', marginBottom: '10px', marginTop: '16px' }}>
        Quick Start Ideas
      </p>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '10px',
      }}>
        {allTiles.map((tile) => {
          // All Quick Start tiles are action prompts, so they share the
          // module's violet accent. (This previously branched on 'insight'
          // tiles whose copy came from demo fixtures — those tiles never
          // existed at render and the fixtures have been removed.)
          const colors = { color: '#7e5fe0', bg: 'rgba(126,95,224,0.08)' };
          const isHovered = hoveredTile === tile.id;

          return (
            <button
              key={tile.id}
              onClick={() => onTileClick(tile.question)}
              onMouseEnter={() => setHoveredTile(tile.id)}
              onMouseLeave={() => setHoveredTile(null)}
              style={{
                textAlign: 'left',
                padding: '12px 14px',
                border: `1px solid ${isHovered ? colors.color : 'var(--border)'}`,
                borderRadius: '10px',
                backgroundColor: isHovered ? colors.bg : 'var(--muted)',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background-color 0.15s',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flex: 1 }}>
                  <span style={{ color: colors.color, flexShrink: 0, display: 'flex', marginTop: '1px' }}>
                    {tile.icon}
                  </span>
                  <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--foreground)', lineHeight: '1.35' }}>
                    {tile.title}
                  </p>
                </div>
                <ArrowRight size={13} style={{ color: isHovered ? colors.color : 'var(--muted-foreground)', transition: 'color 0.15s', flexShrink: 0, marginTop: '3px' }} />
              </div>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', lineHeight: '1.45', paddingLeft: '23px' }}>
                {tile.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
